import { BOTTOM_BAR_ID, BOTTOM_BAR_OPEN_CLASS } from '@/components/bottom-bar/constants'
import { OPEN_MODAL_SELECTOR } from '@/lib/overlayLayers'
import { type RefObject, useEffect, useRef, useState } from 'react'

interface UIOverlayState {
	/**
	 * A modal dialog is open and covers the caller. Pass `elementRef` to be told only about dialogs
	 * the element is not inside: a table living in a dialog is not covered by that dialog, and a
	 * surface it owns (its selection toolbar) has to keep rendering.
	 */
	isDialogOpen: boolean
	isBottomBarOpen: boolean
}

/**
 * Hook to track UI overlay states (dialogs and bottom bar).
 * Detects open ui/dialog and ui/sheet modals by their content slot, not popovers or dropdown menus.
 */
export function useUIOverlayState(elementRef?: RefObject<HTMLElement | null>): UIOverlayState {
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [isBottomBarOpen, setIsBottomBarOpen] = useState(false)
	const rafIdRef = useRef(0)

	useEffect(() => {
		const checkStates = () => {
			const openDialogs = Array.from(document.querySelectorAll(OPEN_MODAL_SELECTOR))
			const element = elementRef?.current
			setIsDialogOpen(openDialogs.some(dialog => !element || !dialog.contains(element)))

			const bottomBar = document.getElementById(BOTTOM_BAR_ID)
			setIsBottomBarOpen(bottomBar?.classList.contains(BOTTOM_BAR_OPEN_CLASS) ?? false)
		}

		// Debounce using RAF to coalesce rapid DOM changes into a single check per frame
		const debouncedCheck = () => {
			cancelAnimationFrame(rafIdRef.current)
			rafIdRef.current = requestAnimationFrame(checkStates)
		}

		checkStates()

		const observer = new MutationObserver(debouncedCheck)
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['class', 'data-state'],
		})

		return () => {
			observer.disconnect()
			cancelAnimationFrame(rafIdRef.current)
		}
	}, [elementRef])

	return { isDialogOpen, isBottomBarOpen }
}
